<template>
  <div class="order-dashboard">
    <!-- Dashboard Header -->
    <div class="dashboard-header">
      <div class="header-content">
        <div>
          <h4 class="dashboard-title">Orders Dashboard</h4>
          <p class="dashboard-subtitle">Real-time order analytics & revenue insights</p>
        </div>
        <div class="date-filter">
          <select class="filter-select" v-model="selectedPeriod">
            <option>Last 7 Days</option>
            <option>Last 30 Days</option>
            <option>Last 90 Days</option>
            <option>YTD 2025</option>
            <option>All Time</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="md-layout stats-row">
      <div class="md-layout-item md-size-25 md-small-size-50">
        <md-card class="stat-card">
          <md-card-content>
            <div class="stat-icon stat-icon-primary">
              <i class="fas fa-shopping-cart"></i>
            </div>
            <div class="stat-content">
              <h3 class="stat-value">{{ formatNumber(stats.totalOrders) }}</h3>
              <p class="stat-label">Total Orders</p>
              <span class="stat-change positive">
                <i class="fas fa-arrow-up"></i> {{ stats.ordersGrowth }}% from last month
              </span>
            </div>
          </md-card-content>
        </md-card>
      </div>

      <div class="md-layout-item md-size-25 md-small-size-50">
        <md-card class="stat-card">
          <md-card-content>
            <div class="stat-icon stat-icon-success">
              <i class="fas fa-dollar-sign"></i>
            </div>
            <div class="stat-content">
              <h3 class="stat-value">{{ formatCurrency(stats.totalRevenue) }}</h3>
              <p class="stat-label">Total Revenue</p>
              <span class="stat-change positive">
                <i class="fas fa-arrow-up"></i> {{ stats.revenueGrowthPercent }}%
              </span>
            </div>
          </md-card-content>
        </md-card>
      </div>

      <div class="md-layout-item md-size-25 md-small-size-50">
        <md-card class="stat-card">
          <md-card-content>
            <div class="stat-icon stat-icon-warning">
              <i class="fas fa-chart-line"></i>
            </div>
            <div class="stat-content">
              <h3 class="stat-value">{{ stats.avgOrderValue }}</h3>
              <p class="stat-label">Avg Order Value</p>
              <span class="stat-change positive">
                <i class="fas fa-arrow-up"></i> {{ stats.aovGrowth }}% ↑
              </span>
            </div>
          </md-card-content>
        </md-card>
      </div>

      <div class="md-layout-item md-size-25 md-small-size-50">
        <md-card class="stat-card">
          <md-card-content>
            <div class="stat-icon stat-icon-info">
              <i class="fas fa-users"></i>
            </div>
            <div class="stat-content">
              <h3 class="stat-value">{{ formatNumber(stats.totalCustomers) }}</h3>
              <p class="stat-label">Total Customers</p>
              <span class="stat-change positive">
                <i class="fas fa-arrow-up"></i> {{ stats.customerGrowth }}% new
              </span>
            </div>
          </md-card-content>
        </md-card>
      </div>
    </div>

    <!-- Charts Grid -->
    <div class="md-layout charts-row">
      <!-- Monthly Orders -->
      <div class="md-layout-item md-size-50 md-small-size-100 chart-item">
        <chart-card
            class="monthly-orders-card"
            header-animation="false"
            :chart-data="monthlyOrdersChart.data"
            :chart-options="monthlyOrdersChart.options"
            :chart-responsive-options="monthlyOrdersChart.responsiveOptions"
            chart-type="Bar"
            chart-inside-header
            no-footer
            background-color="success"
        >
            <template slot="content">
            <h4 class="title">Monthly Orders</h4>
            <p class="category">Orders by Month ({{ selectedPeriod }})</p>
            </template>
        </chart-card>
        </div>



      <!-- Weekly Orders -->
      <div class="md-layout-item md-size-50 md-small-size-100 chart-item">
        <chart-card
          header-animation="false"
          :chart-data="weeklyOrdersChart.data"
          :chart-options="weeklyOrdersChart.options"
          chart-type="Line"
          chart-inside-header
          no-footer
          background-color="warning"
        >
          <template slot="content">
            <h4 class="title">Weekly Orders</h4>
            <p class="category">This Week Performance</p>
          </template>
        </chart-card>
      </div>

      <!-- Revenue Trend -->
      <div class="md-layout-item md-size-33 md-medium-size-50 md-small-size-100 chart-item">
        <chart-card
          header-animation="false"
          :chart-data="revenueTrendChart.data"
          :chart-options="revenueTrendChart.options"
          chart-type="Line"
          header-icon
          chart-inside-content
          no-footer
          background-color="primary"
        >
          <template slot="chartInsideHeader">
            <div class="card-icon">
              <md-icon>trending_up</md-icon>
            </div>
            <h4 class="title">
              Revenue Trend
              <small> &nbsp;Last 4 Weeks</small>
            </h4>
          </template>
        </chart-card>
      </div>

      <!-- Order Status Pie -->
      <div class="md-layout-item md-size-33 md-medium-size-50 md-small-size-100 chart-item">
        <chart-card
          header-animation="false"
          :chart-data="orderStatusChart.data"
          :chart-options="orderStatusChart.options"
          chart-type="Pie"
          header-icon
          chart-inside-content
          background-color="danger"
        >
          <template slot="chartInsideHeader">
            <div class="card-icon">
              <md-icon>pie_chart</md-icon>
            </div>
            <h4 class="title">Order Status</h4>
          </template>
          <template slot="footer">
            <div class="md-layout">
              <div class="md-layout-item">
                <i class="fa fa-circle text-success"></i> Completed (69%)&nbsp;&nbsp;
                <i class="fa fa-circle text-warning"></i> Processing (19%)&nbsp;&nbsp;
                <i class="fa fa-circle text-info"></i> Shipped (12%)
              </div>
            </div>
          </template>
        </chart-card>
      </div>

      <!-- Top Products Bar -->
      <div class="md-layout-item md-size-33 md-medium-size-100 md-small-size-100 chart-item">
        <chart-card
          header-animation="false"
          :chart-data="topProductsChart.data"
          :chart-options="topProductsChart.options"
          :chart-responsive-options="topProductsChart.responsiveOptions"
          chart-type="Bar"
          header-icon
          chart-inside-content
          no-footer
          background-color="success"
        >
          <template slot="chartInsideHeader">
            <div class="card-icon">
              <md-icon>shopping_bag</md-icon>
            </div>
            <h4 class="title">
              Top Products
              <small> &nbsp;This Month</small>
            </h4>
          </template>
        </chart-card>
      </div>
    </div>

    <!-- Additional Stats & Tables -->
    <div class="md-layout bottom-row">
      <!-- Customer Metrics -->
      <div class="md-layout-item md-size-50 md-small-size-100">
        <md-card class="metrics-card">
          <md-card-header>
            <div class="md-title">Customer Metrics</div>
          </md-card-header>
          <md-card-content>
            <div class="metrics-grid">
              <div class="metric-item">
                <h4>{{ formatNumber(customerMetrics.newCustomers) }}</h4>
                <p>New Customers</p>
                <span class="metric-change positive">+{{ customerMetrics.newGrowth }}%</span>
              </div>
              <div class="metric-item">
                <h4>{{ formatNumber(customerMetrics.repeatCustomers) }}</h4>
                <p>Repeat Customers</p>
                <span class="metric-change positive">+{{ customerMetrics.repeatGrowth }}%</span>
              </div>
              <div class="metric-item">
                <h4>{{ customerMetrics.conversionRate }}%</h4>
                <p>Conversion Rate</p>
                <span class="metric-change positive">+{{ customerMetrics.convGrowth }} pts</span>
              </div>
              <div class="metric-item">
                <h4>${{ customerMetrics.avgLifetimeValue }}</h4>
                <p>LTV</p>
                <span class="metric-change positive">+{{ customerMetrics.ltvGrowth }}%</span>
              </div>
            </div>
          </md-card-content>
        </md-card>
      </div>

      <!-- Recent Orders -->
      <div class="md-layout-item md-size-50 md-small-size-100">
        <md-card class="orders-card">
          <md-card-header>
            <div class="md-title">Recent Orders</div>
          </md-card-header>
          <md-card-content>
            <div class="orders-table">
              <div
                v-for="order in recentOrders"
                :key="order.id"
                class="order-row"
              >
                <div class="order-col order-id">#{{ order.id }}</div>
                <div class="order-col order-customer">{{ order.customer }}</div>
                <div class="order-col order-total">${{ formatCurrency(order.total) }}</div>
                <div class="order-col order-status" :class="`status-${order.status}`">
                  {{ order.statusText }}
                </div>
              </div>
            </div>
          </md-card-content>
        </md-card>
      </div>
    </div>
  </div>
</template>

<script>
import { ChartCard } from "@/components";

export default {
  components: {
    ChartCard,
  },
  data() {
    return {
      selectedPeriod: "Last 30 Days",
      stats: {
        totalOrders: 12456,
        ordersGrowth: 18.5,
        totalRevenue: 2458900, // cents
        revenueGrowthPercent: 6.8,
        avgOrderValue: "$197.45",
        aovGrowth: 4.2,
        totalCustomers: 8567,
        customerGrowth: 12.3,
      },
      customerMetrics: {
        newCustomers: 2345,
        newGrowth: 15.2,
        repeatCustomers: 6234,
        repeatGrowth: 9.8,
        conversionRate: 3.45,
        convGrowth: 0.4,
        avgLifetimeValue: "1,247",
        ltvGrowth: 8.7,
      },
      recentOrders: [
        {
          id: "ORD-12456",
          customer: "John Smith",
          total: 45678,
          status: "completed",
          statusText: "Completed",
        },
        {
          id: "ORD-12455",
          customer: "Sarah Johnson",
          total: 23499,
          status: "shipped",
          statusText: "Shipped",
        },
        {
          id: "ORD-12454",
          customer: "Mike Chen",
          total: 78950,
          status: "processing",
          statusText: "Processing",
        },
        {
          id: "ORD-12453",
          customer: "Emily Davis",
          total: 12345,
          status: "pending",
          statusText: "Pending",
        },
        {
          id: "ORD-12452",
          customer: "David Wilson",
          total: 56789,
          status: "completed",
          statusText: "Completed",
        },
      ],
      monthlyOrdersChart: {
        data: {
          labels: [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ],
          series: [
            [890, 1234, 1567, 1789, 2012, 2345, 2567, 2789, 3012, 3456, 3789, 4123],
          ],
        },
        options: {
          seriesBarDistance: 10,
          axisX: {
            showGrid: false,
          },
          height: "250px",
        },
        responsiveOptions: [
          [
            "screen and (max-width: 640px)",
            {
              seriesBarDistance: 5,
              axisX: {
                labelInterpolationFnc(value) {
                  return value[0];
                },
              },
            },
          ],
        ],
      },
      weeklyOrdersChart: {
        data: {
          labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
          series: [[245, 312, 456, 378, 523, 678, 456]],
        },
        options: {
          lineSmooth: this.$Chartist.Interpolation.cardinal({
            tension: 10,
          }),
          axisX: {
            showGrid: false,
          },
          low: 0,
          high: 800,
          showPoint: true,
          height: "250px",
        },
      },
      revenueTrendChart: {
        data: {
          labels: ["Week 1", "Week 2", "Week 3", "Week 4"],
          series: [[45600, 67800, 82300, 101200]],
        },
        options: {
          lineSmooth: this.$Chartist.Interpolation.cardinal({
            tension: 10,
          }),
          axisY: {
            showGrid: true,
            offset: 40,
          },
          axisX: {
            showGrid: false,
          },
          low: 0,
          high: 120000,
          showPoint: true,
          height: "250px",
        },
      },
      orderStatusChart: {
        data: {
          labels: ["Completed", "Processing", "Shipped", "Pending", "Cancelled"],
          series: [69, 19, 12, 5, 1],
        },
        options: {
          height: "230px",
          donut: true,
          donutWidth: 40,
          showLabel: false,
        },
      },
      topProductsChart: {
        data: {
          labels: ["iPhone 16", "MacBook Pro", "AirPods", "Coffee", "Mouse"],
          series: [[789, 456, 1234, 2345, 1567]],
        },
        options: {
          seriesBarDistance: 15,
          axisX: {
            showGrid: false,
          },
          height: "250px",
        },
        responsiveOptions: [
          [
            "screen and (max-width: 640px)",
            {
              seriesBarDistance: 5,
              axisX: {
                labelInterpolationFnc(value) {
                  return value.slice(0, 3);
                },
              },
            },
          ],
        ],
      },
    };
  },
  methods: {
    formatNumber(num) {
      return new Intl.NumberFormat().format(num);
    },
    formatCurrency(num) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
      }).format(num / 100);
    },
  },
};
</script>

<style scoped lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap");
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css");

.order-dashboard {
  font-family: "Inter", sans-serif;
  background: #f3f4f6;
  min-height: 100vh;
  padding: 24px 24px 40px;
}

/* Header */
.dashboard-header {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  padding: 24px 32px;
  border-radius: 18px;
  margin-bottom: 24px;
  box-shadow: 0 12px 30px rgba(16, 185, 129, 0.25);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dashboard-title {
  color: #fff;
  font-size: 2rem;
  font-weight: 800;
  margin: 0 0 4px;
}

.dashboard-subtitle {
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-size: 0.95rem;
}

/* Filter */
.filter-select {
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-weight: 500;
  cursor: pointer;
  outline: none;
}

/* Layout rows */
.stats-row {
  margin-bottom: 16px;
}

.charts-row {
  margin-top: 8px;
  margin-bottom: 16px;
}

.bottom-row {
  margin-top: 8px;
}

.md-layout-item {
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
}

.chart-item {
  z-index: 0;
}

/* Stat cards */
.stat-card {
  border-radius: 18px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.14);
}

.stat-card .md-card-content {
  padding: 20px !important;
  display: flex;
  align-items: center;
  gap: 16px;
}

/* Stat icon */
.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.stat-icon-primary {
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: #fff;
}
.stat-icon-success {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #fff;
}
.stat-icon-warning {
  background: linear-gradient(135deg, #f97316, #ea580c);
  color: #fff;
}
.stat-icon-info {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff;
}

/* Stat text */
.stat-value {
  font-size: 2.1rem;
  font-weight: 800;
  color: #111827;
  margin: 0;
}

.stat-label {
  color: #6b7280;
  font-size: 0.9rem;
  margin: 4px 0 2px;
  font-weight: 500;
}

.stat-change {
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-change.positive {
  color: #16a34a;
}

/* Metrics & orders cards */
.metrics-card,
.orders-card {
  border-radius: 18px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
}

/* Metrics grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.metric-item {
  text-align: center;
  padding: 12px 4px;
}

.metric-item h4 {
  margin: 0 0 4px;
  font-size: 1.6rem;
  font-weight: 700;
  color: #111827;
}

.metric-item p {
  margin: 0 0 4px;
  color: #6b7280;
  font-weight: 500;
  font-size: 0.8rem;
  text-transform: uppercase;
}

.metric-change {
  font-size: 0.8rem;
  font-weight: 600;
  color: #16a34a;
}

/* Orders table */
.orders-table {
  max-height: 280px;
  overflow-y: auto;
}

.order-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e5e7eb;
}

.order-row:last-child {
  border-bottom: none;
}

.order-col {
  flex: 1;
  min-width: 0;
}

.order-id {
  font-weight: 600;
  color: #6b7280;
  font-size: 0.85rem;
}

.order-customer {
  font-weight: 500;
  color: #111827;
}

.order-total {
  font-weight: 700;
  color: #16a34a;
  font-size: 1rem;
}

.order-status {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-completed {
  background: rgba(22, 163, 74, 0.08);
  color: #15803d;
}
.status-shipped {
  background: rgba(37, 99, 235, 0.08);
  color: #2563eb;
}
.status-processing {
  background: rgba(234, 179, 8, 0.08);
  color: #ca8a04;
}
.status-pending {
  background: rgba(75, 85, 99, 0.08);
  color: #4b5563;
}

/* Responsive */
@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}

@media (max-width: 768px) {
  .order-dashboard {
    padding: 16px;
  }
}

.ct-chart-wrapper {
  display: block;
}

.ct-chart-wrapper .ct-chart,
.ct-chart-wrapper .ct-perfect-fourth {
  height: 250px !important;  /* same as monthlyOrdersChart.options.height */
}
.monthly-orders-card ::v-deep .ct-chart,
.monthly-orders-card ::v-deep .ct-perfect-fourth {
  height: 250px !important;
}

</style>
